<script setup lang="ts">
// The egg → adult timeline, drawn to be read at a glance rather than hovered:
// one row per species on a shared day scale, each row a thin line from egg (0)
// to that species' own egg → adult total, with a fat lettered dot at the start
// of every developmental stage — E, 1, 2, 3, 4, P — and the A dot closing the
// line where the adult is reached. A day ruler under the rows ticks off every
// whole day, so a dot's distance to adult is read off the ticks, and the rows
// are directly comparable: the quickest species (Ae. aegypti) visibly stops
// short of the slowest (Cx. tarsalis). No reading depends on a pointer: the
// ruler names each day in a tooltip instead of printing it under the mark.
//
// Each dot still titles itself with its stage and the days to adult from it —
// an affordance and an accessible name, not the only way to read the graphic.
//
// The spacing is a placeholder: the payload carries each species' egg → adult
// total and nothing per stage, so the six developmental stages sit one equal
// share of that total apart — developmentStages.ts carries the detail and the
// caveat the graphic's own tooltip states.
import { computed } from "vue";
import { formatAmount } from "@/components/map-review/charts/theme";
import {
	buildSpeciesStageTracks,
	type DevelopmentStageMark,
} from "@/components/home/developmentStages";
import type { HomeEggToAdult } from "@/type/api";

const props = defineProps<{
	eggToAdult: HomeEggToAdult;
}>();

const timeline = computed(() => buildSpeciesStageTracks(props.eggToAdult));

/** The day the shared scale is drawn out to: the slowest species' total rounded
 *  up to the next whole day, so the ruler ticks land on whole days. A payload
 *  with nothing to draw still needs a span, so the floor is a day. */
const axisDays = computed(() =>
	Math.max(1, Math.ceil(timeline.value.axisMaxDays)),
);

/** Where a number of days from egg sits on the shared scale, as a CSS length. */
function scalePercent(days: number): string {
	return `${(days / axisDays.value) * 100}%`;
}

/** The ruler under the rows: one tick for every whole day the scale spans,
 *  each naming its day on hover and otherwise read as a position. */
const axisTicks = computed(() =>
	Array.from({ length: axisDays.value + 1 }, (_, day) => ({
		day,
		left: scalePercent(day),
	})),
);

/** A dot's own reading: what stage it is, and what is left to run from it —
 *  the adult dot, which has nothing left to run, names only itself. */
function reading(mark: DevelopmentStageMark): string {
	return mark.daysToAdult > 0
		? `${mark.stage} → ${formatAmount(mark.daysToAdult)} days to adult`
		: mark.stage;
}

/** What the whole graphic says about itself, shown on hovering it: how the
 *  ruler and the lines are read, and that the stage spacing is a placeholder.
 *  The ticks and dots title themselves, so this shows only around them. */
const note =
	"Day ruler in whole days from egg; each line ends where its species' adult dot sits. Stage spacing is provisional: an even sixth each, adult being the end point.";
</script>

<template>
	<div
		class="dev-timeline"
		:title="note"
		role="group"
		aria-label="Egg to adult development stages — one line per species on a shared day scale, a lettered dot at each stage from egg to the adult that closes the line"
	>
		<template v-for="track in timeline.tracks" :key="track.species">
			<div
				class="dev-row__species"
				:title="`${track.species} — ${formatAmount(track.days)} days egg → adult`"
			>
				{{ track.species }}
			</div>
			<div class="dev-row__scale">
				<div
					class="dev-row__line"
					:style="{ width: scalePercent(track.days) }"
				></div>
				<span
					v-for="mark in track.marks"
					:key="mark.stage"
					class="dev-mark"
					role="img"
					:style="{ left: scalePercent(mark.offsetDays) }"
					:title="reading(mark)"
					:aria-label="reading(mark)"
				>
					{{ mark.letter }}
				</span>
			</div>
		</template>

		<div
			class="dev-axis__scale"
			role="group"
			:aria-label="`Day ruler from egg, 0 to ${axisDays} days`"
		>
			<span
				v-for="tick in axisTicks"
				:key="tick.day"
				class="dev-axis__tick"
				:style="{ left: tick.left }"
				:title="`Day ${tick.day}`"
			></span>
		</div>
	</div>
</template>

<style scoped>
/* One grid, not a row per species: the name column is as wide as the longest
 * species name and the tracks all start on the same vertical, every row exactly
 * one line tall. The card leaves it half its column, beside the headline figure
 * rather than across the page. */
.dev-timeline {
	display: grid;
	grid-template-columns: max-content minmax(0, 1fr);
	column-gap: 0.4rem;
	row-gap: 0.2rem;
	max-width: 50%;
}

/* Whole species names only; the column is sized to the longest, so nowrap
 * cannot overflow it. */
.dev-row__species {
	grid-column: 1;
	white-space: nowrap;
	font-size: 0.72rem;
	line-height: 1.4rem;
	color: var(--bs-secondary-color);
}

/* The scale itself: 0 at its left edge, the day the ruler rounds up to at its
 * right. The inline margin is room for the half of each end dot that overhangs
 * it — an egg dot centred on 0, and the last day label under the end. */
.dev-row__scale {
	position: relative;
	height: 1.4rem;
	margin-inline: 0.65rem;
}

/* The species' own span, thin enough to read as a line rather than a bar. */
.dev-row__line {
	position: absolute;
	top: 50%;
	left: 0;
	height: 2px;
	transform: translateY(-50%);
	border-radius: 1px;
	background: var(--bs-secondary-border-subtle);
}

/* Fat enough to hold its letter at a glance, centred on the start of its
 * stage — and on the line's end for the adult. */
.dev-mark {
	position: absolute;
	top: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 1.25rem;
	height: 1.25rem;
	transform: translate(-50%, -50%);
	border: 1px solid var(--bs-secondary-border-subtle);
	border-radius: 50%;
	background: var(--bs-secondary-bg);
	color: var(--bs-body-color);
	font-size: 0.62rem;
	font-weight: 600;
	line-height: 1;
}

/* The faint baseline under the rows, with a tick hanging below it at every
 * whole day the scale spans. A tick is its ::before and nothing else — the day
 * it stands for is a tooltip — so the flex parent is what blockifies the mark
 * and gives it its 1px width. */
.dev-axis__scale {
	position: relative;
	grid-column: 2;
	height: 0.9rem;
	margin-inline: 0.65rem;
	border-top: 1px solid var(--bs-border-color);
}

.dev-axis__tick {
	position: absolute;
	top: 0;
	display: flex;
	transform: translateX(-50%);
}

.dev-axis__tick::before {
	content: "";
	width: 1px;
	height: 0.2rem;
	background: var(--bs-border-color);
}
</style>
